import { FormattedNumber } from '@liveauctioneers/hammer-ui-core/intl';
import { BodySecondary, H5 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback } from 'react';

import { RouteLink } from '@/components/RouteLinks/RouteLinks';
import { useAppDispatch } from '@/redux/hooks';
import { fetchSavedSearchItemsIfNeeded } from '@/redux/modules/savedSearch';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { addImageParams, getCategoryPageUrl } from '@/utils/urls';

import FollowSearchButton from './FollowSearchButton';

type Props = {
    categoryId: string;
    categoryUrl: string;
    followerCount: number;
    images: string[];
    itemCount: number;
    name: string;
    saveSearchStateKey: string;
    shouldUseHomePageRedesign?: boolean;
};

export const SearchFollowSuggestionsCard = ({
    categoryId,
    categoryUrl,
    followerCount,
    images,
    itemCount,
    name,
    saveSearchStateKey,
    shouldUseHomePageRedesign = false,
}: Props) => {
    const dispatch = useAppDispatch();
    const formatMessage = useAppFormatMessage();

    const trackClick = useCallback(() => {
        dispatch(fetchSavedSearchItemsIfNeeded(true));
    }, [dispatch]);

    return (
        <div
            className={cn(
                'border border-border-secondary [&_a]:no-underline',
                shouldUseHomePageRedesign
                    ? 'flex w-[280px] min-w-[280px] shrink-0 flex-col overflow-hidden rounded-md smMin:w-[381px] smMin:min-w-[381px]'
                    : 'h-[215px] w-[285px] min-w-[285px]'
            )}
        >
            <RouteLink to={`${getCategoryPageUrl(categoryUrl, categoryId)}`}>
                <div
                    className={cn(
                        'flex border-b border-border-secondary bg-background-primary',
                        shouldUseHomePageRedesign && 'overflow-hidden'
                    )}
                >
                    {images.map((imageUrl, index) => (
                        <div
                            className={cn(
                                'relative flex items-center justify-center overflow-hidden bg-background-secondary',
                                shouldUseHomePageRedesign
                                    ? cn(
                                          'h-[93px] min-w-0 flex-1 smMin:h-[127px]',
                                          index < images.length - 1 && 'border-r border-r-background-primary'
                                      )
                                    : 'mr-[1px] h-[72px] w-[94px] bg-[#e0dad3] last:mr-0'
                            )}
                            key={`thumbnail-${index}`}
                        >
                            <img
                                alt={`${name} - ${index + 1} - image`}
                                className={cn(
                                    shouldUseHomePageRedesign
                                        ? 'size-full object-cover'
                                        : 'max-h-[calc(100%+16px)] max-w-[calc(100%+16px)]'
                                )}
                                loading="lazy"
                                src={addImageParams({
                                    url: imageUrl,
                                    width: shouldUseHomePageRedesign ? 254 : 188,
                                })}
                            />
                        </div>
                    ))}
                </div>
                <div
                    className={cn(
                        shouldUseHomePageRedesign ? 'flex flex-col gap-2xs p-sm pb-0' : 'mx-[13px] mt-[15px]'
                    )}
                >
                    {shouldUseHomePageRedesign ? (
                        <H5 className="truncate text-left">{name}</H5>
                    ) : (
                        <div className="font-secondary text-[15px] leading-[21px] tracking-[0.5px] text-text-primary">
                            {name}
                        </div>
                    )}
                    <BodySecondary
                        className={cn(!shouldUseHomePageRedesign && 'mt-[6px] tracking-[0.5px]')}
                        color="secondary"
                    >
                        <FormattedNumber
                            key="search-follow-suggestions-card-item-count"
                            style="decimal"
                            value={itemCount}
                        />{' '}
                        {formatMessage(['searchFollowSuggestions', 'items'])}{' '}
                        <FormattedNumber
                            key="search-follow-suggestions-card-follower-count"
                            style="decimal"
                            value={followerCount}
                        />{' '}
                        {formatMessage(['searchFollowSuggestions', 'followers'])}
                    </BodySecondary>
                </div>
            </RouteLink>
            <div
                className={cn(
                    '[&_button[class*="button-saved"]]:cursor-auto',
                    shouldUseHomePageRedesign
                        ? 'mt-auto p-sm [&_button]:w-full'
                        : 'mt-[15px] ml-[13px] [&_button]:w-[258px]'
                )}
            >
                <FollowSearchButton
                    categoryId={categoryId}
                    onClick={trackClick}
                    saveSearchKey={saveSearchStateKey}
                />
            </div>
        </div>
    );
};
